import { useId, useState } from "react";

import { KnowledgeBase, MarketplaceOffer, OfferStatus, OfferStatusI18N } from "@the-real-insight/tri-model";
import { userAPI } from "../UserAPI";
import { useModalContext } from "../components/ModalProvider";
import { useAtom } from "jotai";
import { authenticationAtom } from "../State";
import { useTranslation } from "react-i18next";
import { configuration } from "../Configuration";

import { Archive, Clock, History, Pencil, RefreshCw, Rocket, Trash2, UserPlus, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
interface OfferStatusManagementProps {
    marketplaceOffer: KnowledgeBase | MarketplaceOffer;
    updateSelectedMarketplaceOffer: (knowledgeBase: any) => void;
}

interface OfferStatusDisplayProps {
    marketplaceOffer: KnowledgeBase | MarketplaceOffer;
    updateSelectedMarketplaceOffer: (knowledgeBase: any) => void;
    appealReason: string;
    setAppealReason: (reason: string) => void;
    eventHistoryDialogOpen: boolean;
    setEventHistoryDialogOpen: (open: boolean) => void;
}

function OfferStatusDisplay({
    marketplaceOffer,
    updateSelectedMarketplaceOffer,
    appealReason: _appealReason,
    setAppealReason: _setAppealReason,
    eventHistoryDialogOpen,
    setEventHistoryDialogOpen
}: OfferStatusDisplayProps) {
    const { t } = useTranslation();
    const [authentication] = useAtom(authenticationAtom) as any;
    const { pushError, showDeleteDialog: _showDeleteDialog } = useModalContext() as any;

    const renderEventHistory = () => {
        if (!marketplaceOffer || !marketplaceOffer.lastEvent()) {
            return null;
        }

        return (
            <div>
                <div className="flex flex-wrap items-start gap-2 text-muted-foreground">
                    {t('offerStatusManagement.lastUpdate', {
                        date: new Date(marketplaceOffer.lastEvent()?.timestamp || 0).toLocaleDateString('de-DE') + ' ' + new Date(marketplaceOffer.lastEvent()?.timestamp || 0).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
                        firstName: marketplaceOffer.lastEvent()?.author?.firstName,
                        lastName: marketplaceOffer.lastEvent()?.author?.lastName
                    })}
                    <Button
                        type="button"
                        variant="ghost"
                        className="text-muted-foreground"
                        onClick={() => setEventHistoryDialogOpen(true)}
                    >
                        <History className="mr-2 h-4 w-4" aria-hidden />
                        {t('offerStatusManagement.history')}
                    </Button>
                </div>
                {marketplaceOffer.lastEvent()?.timestamp && marketplaceOffer.updated && (new Date(marketplaceOffer.lastEvent()?.timestamp || 0).getTime()) < new Date(marketplaceOffer.updated as any).getTime() && (
                    <div className="flex flex-wrap items-start gap-2">
                        <div>{t('offerStatusManagement.unpublishedChanges')}</div>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={async () => {
                                try {
                                    updateSelectedMarketplaceOffer(await userAPI.publishMarketplaceOffer(authentication.customer, marketplaceOffer));
                                } catch (error) {
                                    pushError(error);
                                }
                            }}
                        >
                            <RefreshCw className="mr-2 h-4 w-4" aria-hidden />
                            {t('offerStatusManagement.publishChanges')}
                        </Button>
                    </div>
                )}
            </div>
        );
    };

    const renderEventHistoryDialog = () => (
        <Dialog
            open={eventHistoryDialogOpen}
            onOpenChange={(open) => {
                if (!open) {
                    setEventHistoryDialogOpen(false);
                }
            }}
        >
            <DialogContent
                className="max-w-[calc(100vw-2rem)] sm:max-w-[800px]"
                onInteractOutside={(event) => event.preventDefault()}
                onEscapeKeyDown={(event) => event.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle>{t('offerStatusManagement.eventHistory')}</DialogTitle>
                </DialogHeader>
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[180px] font-bold text-foreground">
                                    {t('offerStatusManagement.status')}
                                </TableHead>
                                <TableHead className="w-[180px] font-bold text-foreground">
                                    {t('offerStatusManagement.timestamp')}
                                </TableHead>
                                <TableHead className="font-bold text-foreground">
                                    {t('offerStatusManagement.user')}
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {marketplaceOffer.events && marketplaceOffer.events.length > 0 ? (
                                marketplaceOffer.events.map((event: any, index: number) => (
                                    <TableRow key={index}>
                                        <TableCell className="font-bold">
                                            {OfferStatusI18N['de'][(event.status || event.type) as OfferStatus]}
                                        </TableCell>
                                        <TableCell>
                                            {new Date(event.timestamp || 0).toLocaleString()}
                                        </TableCell>
                                        <TableCell>
                                            {event.author?.firstName} {event.author?.lastName}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={3} className="text-center text-muted-foreground">
                                        {t('offerStatusManagement.noEventsFound')}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
                <DialogFooter className="gap-2 pt-4 sm:gap-0">
                    <Button type="button" variant="outline" onClick={() => setEventHistoryDialogOpen(false)}>
                        {t('global.close')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );

    return (
        <>
            {renderEventHistory()}
            {renderEventHistoryDialog()}
        </>
    );
}

/**
 * "Intern verfügbar" (`published`): whether the customer's own users get the offer. Independent of
 * the marketplace, so it sits in "Allgemeine Informationen" of each editor and stays visible when
 * the marketplace sections are switched off.
 */
export function InternallyAvailableCheckbox({
    marketplaceOffer,
    updateSelectedMarketplaceOffer
}: OfferStatusManagementProps) {
    const { t } = useTranslation();
    const fieldId = useId();

    return (
        <div className="flex items-center gap-2">
            <Checkbox
                id={`${fieldId}-internallyAvailable`}
                checked={!!marketplaceOffer.published}
                onCheckedChange={(checked) => {
                    // Preserve the offer's real type (PromptTool, KnowledgeBase,
                    // AgenticWorkflow, ...) instead of hardcoding KnowledgeBase,
                    // which would overwrite _type for non-KnowledgeBase offers.
                    const OfferClass = marketplaceOffer.getTypeClass();
                    const updatedOffer = new OfferClass({ ...marketplaceOffer, published: checked === true });
                    updateSelectedMarketplaceOffer(updatedOffer);
                }}
            />
            <Label htmlFor={`${fieldId}-internallyAvailable`}>
                {t('offerStatusManagement.internallyAvailable')}
            </Label>
        </div>
    );
}

export default function OfferStatusManagement({
    marketplaceOffer,
    updateSelectedMarketplaceOffer
}: OfferStatusManagementProps) {
    const { t } = useTranslation();
    const fieldId = useId();
    const [authentication] = useAtom(authenticationAtom) as any;
    const { pushError, showDeleteDialog, showMessageDialog } = useModalContext() as any;
    const [appealReason, setAppealReason] = useState('');
    const [eventHistoryDialogOpen, setEventHistoryDialogOpen] = useState(false);

    const validateMarketplaceOffer = (offer: any) => {
        const missingParts = [];

        if (!offer.contactEmail) {
            missingParts.push(t('offerStatusManagement.missingContactEmail'));
        }

        if (!offer.urlTermsAndConditions && !offer.termsAndConditionsURL) {
            missingParts.push(t('offerStatusManagement.missingTermsAndConditionsURL'));
        }

        if (!offer.urlPrivacyPolicy && !offer.privacyPolicyURL) {
            missingParts.push(t('offerStatusManagement.missingDataProtectionURL'));
        }

        if (!offer.synopsis || offer.synopsis.length <= 200) {
            missingParts.push(t('offerStatusManagement.missingSynopsis'));
        }

        if (!offer.marketingContent || offer.marketingContent.length <= 1000) {
            missingParts.push(t('offerStatusManagement.missingMarketingContent'));
        }

        if (!offer.images || offer.images.length === 0) {
            missingParts.push(t('offerStatusManagement.missingImages'));
        }

        return missingParts;
    };

    // "Im Marktplatz angeboten" stays locked once the offer is in review,
    // live, or retired — the offer-status state machine drives that flag via
    // the buttons on the right. "Intern verfügbar" is independent of the
    // marketplace status and lives in "Allgemeine Informationen"
    // (InternallyAvailableCheckbox below).
    const isMarketplaceOfferedDisabled = configuration.mode !== "test" && (
        marketplaceOffer.lastStatus === OfferStatus.submitted ||
        marketplaceOffer.lastStatus === OfferStatus.approved ||
        marketplaceOffer.lastStatus === OfferStatus.published ||
        marketplaceOffer.lastStatus === OfferStatus.discontinued
    );

    return (
        <>
            <div className="flex min-w-0 flex-wrap items-start gap-8">
                <div className="mt-4 flex flex-col gap-4">
                    <div className={cn('flex items-center gap-2', isMarketplaceOfferedDisabled && 'opacity-60')}>
                        <Checkbox
                            id={`${fieldId}-offeredInMarketplace`}
                            checked={!!marketplaceOffer.offered}
                            disabled={isMarketplaceOfferedDisabled}
                            onCheckedChange={(checked) => {
                                const OfferClass = marketplaceOffer.getTypeClass();
                                const updatedOffer = new OfferClass({ ...marketplaceOffer, offered: checked === true });
                                updateSelectedMarketplaceOffer(updatedOffer);
                            }}
                        />
                        <Label htmlFor={`${fieldId}-offeredInMarketplace`}>
                            {t('offerStatusManagement.offeredInMarketplace')}
                        </Label>
                    </div>
                </div>
                {
                    (() => {
                        switch (marketplaceOffer.lastStatus) {
                            case OfferStatus.draft:
                                return (
                                    <div className="min-w-0">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            className="text-success hover:text-success"
                                            onClick={async () => {
                                                const missingParts = validateMarketplaceOffer(marketplaceOffer);
                                                if (missingParts.length > 0) {
                                                    showMessageDialog(
                                                        t('offerStatusManagement.missingMarketplaceSubmissionRequirementsTitle'),
                                                        <div>
                                                            <div>{t('offerStatusManagement.missingMarketplaceSubmissionRequirementsMessage')}</div>
                                                            <ul className="mt-2 list-disc pl-5">
                                                                {missingParts.map((part, index) => <li key={index}>{part}</li>)}
                                                            </ul>
                                                        </div>
                                                    );
                                                    return;
                                                }

                                                try {
                                                    const newKnowledgeBase = await userAPI.submitMarketplaceOffer(authentication.customer, marketplaceOffer);
                                                    updateSelectedMarketplaceOffer(newKnowledgeBase);
                                                } catch (error) {
                                                    pushError(error);
                                                }
                                            }}
                                        >
                                            <UserPlus className="mr-2 h-4 w-4" aria-hidden />
                                            {t('offerStatusManagement.submitForReview')}
                                        </Button>
                                        <OfferStatusDisplay
                                            marketplaceOffer={marketplaceOffer}
                                            updateSelectedMarketplaceOffer={updateSelectedMarketplaceOffer}
                                            appealReason={appealReason}
                                            setAppealReason={setAppealReason}
                                            eventHistoryDialogOpen={eventHistoryDialogOpen}
                                            setEventHistoryDialogOpen={setEventHistoryDialogOpen}
                                        />
                                    </div>
                                );
                            case OfferStatus.submitted:
                                return (
                                    <div className="min-w-0">
                                        <OfferStatusDisplay
                                            marketplaceOffer={marketplaceOffer}
                                            updateSelectedMarketplaceOffer={updateSelectedMarketplaceOffer}
                                            appealReason={appealReason}
                                            setAppealReason={setAppealReason}
                                            eventHistoryDialogOpen={eventHistoryDialogOpen}
                                            setEventHistoryDialogOpen={setEventHistoryDialogOpen}
                                        />
                                        <div className="flex items-center gap-2 text-warning">
                                            <Clock className="h-4 w-4" aria-hidden />
                                            {t('offerStatusManagement.beingReviewed')}
                                        </div>
                                        <div>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                onClick={async () => {
                                                    try {
                                                        updateSelectedMarketplaceOffer(await userAPI.refreshMarketplaceOffer(authentication.customer, marketplaceOffer));
                                                    } catch (error) {
                                                        pushError(error);
                                                    }
                                                }}
                                            >
                                                <RefreshCw className="mr-2 h-4 w-4" aria-hidden />
                                                {t('offerStatusManagement.refreshStatus')}
                                            </Button>
                                        </div>
                                    </div>
                                );
                            case OfferStatus.approved:
                                return (
                                    <div className="min-w-0">
                                        <OfferStatusDisplay
                                            marketplaceOffer={marketplaceOffer}
                                            updateSelectedMarketplaceOffer={updateSelectedMarketplaceOffer}
                                            appealReason={appealReason}
                                            setAppealReason={setAppealReason}
                                            eventHistoryDialogOpen={eventHistoryDialogOpen}
                                            setEventHistoryDialogOpen={setEventHistoryDialogOpen}
                                        />
                                        <div>
                                            {t('offerStatusManagement.solutionApproved')}
                                        </div>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            onClick={async () => {
                                                try {
                                                    updateSelectedMarketplaceOffer(await userAPI.publishMarketplaceOffer(authentication.customer, marketplaceOffer));
                                                } catch (error) {
                                                    pushError(error);
                                                }
                                            }}
                                        >
                                            <Rocket className="mr-2 h-4 w-4" aria-hidden />
                                            {t('offerStatusManagement.publishInStore')}
                                        </Button>
                                    </div>
                                );
                            case OfferStatus.published:
                                return (
                                    <div className="min-w-0">
                                        <OfferStatusDisplay
                                            marketplaceOffer={marketplaceOffer}
                                            updateSelectedMarketplaceOffer={updateSelectedMarketplaceOffer}
                                            appealReason={appealReason}
                                            setAppealReason={setAppealReason}
                                            eventHistoryDialogOpen={eventHistoryDialogOpen}
                                            setEventHistoryDialogOpen={setEventHistoryDialogOpen}
                                        />
                                        <div>
                                            {t('offerStatusManagement.solutionAvailable')}
                                        </div>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            className="text-destructive hover:text-destructive"
                                            onClick={async () => {
                                                showDeleteDialog(
                                                    t('offerStatusManagement.removeFromStoreTitle'),
                                                    t('offerStatusManagement.remove'),
                                                    async () => {
                                                        try {
                                                            updateSelectedMarketplaceOffer(await userAPI.unpublishMarketplaceOffer(authentication.customer, marketplaceOffer));
                                                        } catch (error) {
                                                            pushError(error);
                                                        }
                                                    }
                                                );
                                            }}
                                        >
                                            <Trash2 className="mr-2 h-4 w-4" aria-hidden />
                                            {t('offerStatusManagement.removeFromStore')}
                                        </Button>
                                    </div>
                                );
                            case OfferStatus.rejected:
                                return <div className="min-w-0">
                                    <OfferStatusDisplay
                                        marketplaceOffer={marketplaceOffer}
                                        updateSelectedMarketplaceOffer={updateSelectedMarketplaceOffer}
                                        appealReason={appealReason}
                                        setAppealReason={setAppealReason}
                                        eventHistoryDialogOpen={eventHistoryDialogOpen}
                                        setEventHistoryDialogOpen={setEventHistoryDialogOpen}
                                    />
                                    <div>
                                        <div className="flex items-center gap-2 text-destructive">
                                            <X className="h-4 w-4" aria-hidden />
                                            {t('offerStatusManagement.offerRejected')}
                                        </div>
                                        <div className="mt-3 text-sm">
                                            <span className="font-bold">{t('offerStatusManagement.reason')}</span><br />{marketplaceOffer.rejectionReason}
                                        </div>
                                    </div>
                                    <div className="mt-3">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            disabled={!appealReason}
                                            onClick={async () => {
                                                const missingParts = validateMarketplaceOffer(marketplaceOffer);
                                                if (missingParts.length > 0) {
                                                    showMessageDialog(
                                                        t('offerStatusManagement.missingMarketplaceSubmissionRequirementsTitle'),
                                                        <div>
                                                            <div>{t('offerStatusManagement.missingMarketplaceSubmissionRequirementsMessage')}</div>
                                                            <ul className="mt-2 list-disc pl-5">
                                                                {missingParts.map((part, index) => <li key={index}>{part}</li>)}
                                                            </ul>
                                                        </div>
                                                    );
                                                    return;
                                                }

                                                try {
                                                    updateSelectedMarketplaceOffer(await userAPI.appealAfterOfferRejection(authentication.customer, marketplaceOffer, appealReason));
                                                } catch (error) {
                                                    pushError(error);
                                                }
                                            }}
                                        >
                                            <Pencil className="mr-2 h-4 w-4" aria-hidden />
                                            {t('offerStatusManagement.resubmitChangedOffer')}
                                        </Button>
                                        <div>
                                            <div className="flex max-w-2xl flex-col gap-1.5">
                                                <Label htmlFor={`${fieldId}-appealReason`}>
                                                    {t('offerStatusManagement.reasonForResubmission')}
                                                </Label>
                                                <Textarea
                                                    id={`${fieldId}-appealReason`}
                                                    rows={5}
                                                    value={appealReason}
                                                    onChange={(event) => {
                                                        setAppealReason(event.target.value || '');
                                                    }}
                                                />
                                            </div>
                                            <div className="text-xs text-muted-foreground">{appealReason.length}/200</div>
                                        </div>
                                    </div>
                                </div>;
                            case OfferStatus.discontinued:
                                return (
                                    <div className="min-w-0">
                                        <OfferStatusDisplay
                                            marketplaceOffer={marketplaceOffer}
                                            updateSelectedMarketplaceOffer={updateSelectedMarketplaceOffer}
                                            appealReason={appealReason}
                                            setAppealReason={setAppealReason}
                                            eventHistoryDialogOpen={eventHistoryDialogOpen}
                                            setEventHistoryDialogOpen={setEventHistoryDialogOpen}
                                        />
                                        <div className="flex items-center gap-2 text-destructive">
                                            <Archive className="h-4 w-4" aria-hidden />
                                            {t('offerStatusManagement.offerNoLongerAvailable')}
                                        </div>
                                    </div>
                                );
                            default:
                                return (
                                    <OfferStatusDisplay
                                        marketplaceOffer={marketplaceOffer}
                                        updateSelectedMarketplaceOffer={updateSelectedMarketplaceOffer}
                                        appealReason={appealReason}
                                        setAppealReason={setAppealReason}
                                        eventHistoryDialogOpen={eventHistoryDialogOpen}
                                        setEventHistoryDialogOpen={setEventHistoryDialogOpen}
                                    />
                                );
                        }
                    })()
                }
            </div>
        </>
    );
}
